/**
 * Searchable Category / Folder picker for the Freshdesk Solutions tree.
 *
 * Folders are Freshdesk's sub-categories, so this collapses the two levels into a
 * single searchable dropdown: a category (bold, top-level) or one of its nested
 * folders (indented). Reused by the Solutions Articles/Folders filters and the
 * exception-link modal, so it is purely presentational - the caller supplies the
 * (optionally market-scoped) `categories` + `folders` and owns the selection.
 *
 * Selection is the lightweight `{ type, id }` tuple both consumers persist (URL
 * params on the Solutions tab; option pre-filtering in the modal).
 */
import React, { useMemo, useState } from 'react'
import { Autocomplete, Box, TextField, Typography, createFilterOptions } from '@mui/material'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'

import FilterFieldLabel from '../filters/fields/FilterFieldLabel'
import type {
  FreshdeskSolutionCategory,
  FreshdeskSolutionFolder,
} from '../../../services/admin/integrationsApi'
import { buildSolutionsTree, type SolutionsTreeNode } from './solutionsTree'

export type CategoryFolderValue =
  | { type: 'category' | 'folder'; id: number }
  | null

type CategoryFolderOption = SolutionsTreeNode

interface Props {
  categories: FreshdeskSolutionCategory[]
  folders: FreshdeskSolutionFolder[]
  value: CategoryFolderValue
  onChange: (value: CategoryFolderValue) => void
  /** 'both' lists categories + nested folders; 'category' lists categories only. */
  mode?: 'both' | 'category'
  /** Restrict options to one market (ANZ/USA). */
  market?: 'ANZ' | 'USA' | null
  /** Label rendered above the control (like the Market filter). */
  label?: string
  placeholder?: string
  size?: 'small' | 'medium'
  /** Disable the control (e.g. until a market is chosen). */
  disabled?: boolean
  sx?: object
}

const filterOptions = createFilterOptions<CategoryFolderOption>({
  stringify: (o) => o.searchText,
})

/**
 * Collapsed-state display for the selected option: a bold Category > Folder
 * breadcrumb for a folder (matching the inline breadcrumb on the Solutions tab),
 * or just the bold category name for a category / orphan folder.
 */
const renderSelectedBreadcrumb = (o: CategoryFolderOption): React.ReactNode => {
  const hasParent = o.type === 'folder' && !!o.category
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        maxWidth: '100%',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      {hasParent && (
        <>
          <Typography
            variant="body2"
            component="span"
            sx={{ fontWeight: 700, color: 'text.primary' }}
          >
            {o.category}
          </Typography>
          <Typography variant="body2" component="span" sx={{ color: 'text.disabled' }}>
            ›
          </Typography>
        </>
      )}
      <Typography
        variant="body2"
        component="span"
        sx={{
          fontWeight: hasParent ? 400 : 700,
          color: hasParent ? 'text.secondary' : 'text.primary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {o.label}
      </Typography>
    </Box>
  )
}

const CategoryFolderSelect: React.FC<Props> = ({
  categories,
  folders,
  value,
  onChange,
  mode = 'both',
  market,
  label,
  placeholder = 'All categories/folders',
  size = 'small',
  disabled = false,
  sx,
}) => {
  const options = useMemo(
    () => buildSolutionsTree(categories, folders, market, mode),
    [categories, folders, mode, market],
  )

  const selected = useMemo(
    () =>
      value
        ? options.find((o) => o.type === value.type && o.id === value.id) ?? null
        : null,
    [options, value],
  )

  // Rich collapsed display: while the menu is closed the text input is blanked
  // and the selection shows as a bold Category > Folder breadcrumb in the start
  // adornment; while open the user's typed text drives the (searchText) filter.
  const [open, setOpen] = useState(false)
  const [typed, setTyped] = useState('')
  const showSelected = !!selected && !open

  return (
    <Box sx={{ minWidth: 300, ...sx }}>
      {label && <FilterFieldLabel>{label}</FilterFieldLabel>}
      <Autocomplete
        options={options}
        value={selected}
        size={size}
        fullWidth
        disabled={disabled}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        // Blank while closed (only the breadcrumb adornment shows); typed text
        // while open so it can drive the filter.
        inputValue={open ? typed : ''}
        onInputChange={(_, next, reason) => {
          if (reason === 'input') setTyped(next)
          else if (reason === 'clear') setTyped('')
        }}
        onChange={(_, opt) => {
          setTyped('')
          onChange(opt ? { type: opt.type, id: opt.id } : null)
        }}
        filterOptions={filterOptions}
        // Breadcrumb string is the accessible value (folders read "Category >
        // Folder"); the visible collapsed chrome is the adornment below.
        getOptionLabel={(o) =>
          o.type === 'folder' && o.category ? `${o.category} › ${o.label}` : o.label
        }
        isOptionEqualToValue={(o, v) => o.type === v.type && o.id === v.id}
        renderOption={(props, o) => {
          const isCategory = o.type === 'category'
          // Indent the inner content (not the <li>): MUI's `.MuiAutocomplete-option`
          // sets a `padding` shorthand that would override a `pl` on the option,
          // so folders are nested via the inner row's left margin instead.
          return (
            <Box component="li" {...props} key={`${o.type}-${o.id}`}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  ml: isCategory ? 0 : 3,
                }}
              >
                {isCategory ? (
                  <AccountTreeOutlinedIcon
                    fontSize="small"
                    sx={{ color: 'text.primary' }}
                  />
                ) : (
                  <FolderOutlinedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                )}
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    fontWeight: isCategory ? 700 : 400,
                    color: isCategory ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {o.label}
                </Typography>
              </Box>
            </Box>
          )
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={showSelected ? undefined : placeholder}
            InputProps={{
              ...params.InputProps,
              startAdornment: showSelected
                ? renderSelectedBreadcrumb(selected as CategoryFolderOption)
                : params.InputProps.startAdornment,
            }}
          />
        )}
      />
    </Box>
  )
}

export default CategoryFolderSelect
